import React, { useEffect, useState, useRef } from 'react';
import { Container, Form, Card, Alert, Spinner } from 'react-bootstrap';
import { useSettings } from '../contexts/SettingsContext';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { subscribeToNewsletter, unsubscribeFromNewsletter, checkSubscriptionStatus } from '../services/newsletterService';
import SEO from '../components/SEO';

const Settings = () => {
  const { t } = useTranslation();
  const { 
    voiceURI, 
    rate, 
    pitch, 
    volume, 
    email,
    subscription,
    chatgptApiKey,
    anchorText,
    updateVoiceURI, 
    updateRate, 
    updatePitch, 
    updateVolume,
    updateEmail,
    updateSubscription,
    updateChatGPTApiKey,
    updateAnchorText,
  } = useSettings();
  const [voices, setVoices] = useState([]);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [subscriptionMessage, setSubscriptionMessage] = useState({ type: '', text: '' });
  const previousEmailRef = useRef(email);
  const isInitialMount = useRef(true);

  useEffect(() => {
    const loadVoices = () => {
      if (!("speechSynthesis" in window)) {
        return;
      }
      const v = window.speechSynthesis.getVoices();
      setVoices(v);
      // If no voice is selected and voices are available, set the first one
      if (!voiceURI && v.length) {
        const defaultVoice = v.find(voice => voice.default) || v[0];
        updateVoiceURI(defaultVoice.voiceURI);
      }
    };

    loadVoices();
    if (window.speechSynthesis) {
      window.speechSynthesis.onvoiceschanged = loadVoices;
    }

    return () => {
      if (window.speechSynthesis) {
        window.speechSynthesis.onvoiceschanged = null;
      }
    };
  }, [voiceURI, updateVoiceURI]);

  // Check subscription status when email changes
  useEffect(() => {
    const checkStatus = async () => {
      // Skip on initial mount if email is empty
      if (isInitialMount.current) {
        isInitialMount.current = false;
        if (!email) return;
      }

      // Only check if email has actually changed
      if (email && email !== previousEmailRef.current) {
        previousEmailRef.current = email;
        
        // Validate email format
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
          setSubscriptionMessage({ type: 'warning', text: 'Please enter a valid email address to check subscription status.' });
          return;
        }

        setCheckingStatus(true);
        setSubscriptionMessage({ type: '', text: '' });
        
        try {
          const status = await checkSubscriptionStatus(email);
          updateSubscription(status.subscribed);
          
          if (status.exists && status.subscribed) {
            setSubscriptionMessage({ type: 'success', text: 'You are currently subscribed to the newsletter.' });
          } else if (status.exists && !status.subscribed) {
            setSubscriptionMessage({ type: 'info', text: 'You are not currently subscribed to the newsletter.' });
          }
        } catch (error) {
          console.error('Error checking subscription status:', error);
          // Don't show error on check, just silently update
        } finally {
          setCheckingStatus(false);
        }
      } else if (!email) {
        // Email cleared, reset subscription
        updateSubscription(false);
        setSubscriptionMessage({ type: '', text: '' });
      }
    };

    checkStatus();
  }, [email, updateSubscription]);

  // Handle subscription toggle
  const handleSubscriptionToggle = async (newSubscriptionValue) => {
    if (!email) {
      setSubscriptionMessage({ type: 'danger', text: 'Please enter an email address first.' });
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setSubscriptionMessage({ type: 'danger', text: 'Please enter a valid email address.' });
      return;
    }

    setSubscriptionLoading(true);
    setSubscriptionMessage({ type: '', text: '' });

    try {
      let result;
      if (newSubscriptionValue) {
        // Subscribe
        result = await subscribeToNewsletter(email, 'settings', 'v1.0');
        if (result.success) {
          updateSubscription(true);
          setSubscriptionMessage({ type: 'success', text: result.message || 'Successfully subscribed to newsletter!' });
        } else {
          updateSubscription(false);
          setSubscriptionMessage({ type: 'danger', text: result.message || 'Failed to subscribe. Please try again.' });
        }
      } else {
        // Unsubscribe
        result = await unsubscribeFromNewsletter(email);
        if (result.success) {
          updateSubscription(false);
          setSubscriptionMessage({ type: 'success', text: result.message || 'Successfully unsubscribed from newsletter.' });
        } else {
          updateSubscription(true);
          setSubscriptionMessage({ type: 'danger', text: result.message || 'Failed to unsubscribe. Please try again.' });
        }
      }
    } catch (error) {
      console.error('Subscription error:', error);
      // Revert the toggle on error
      updateSubscription(!newSubscriptionValue);
      setSubscriptionMessage({ type: 'danger', text: 'An error occurred. Please try again later.' });
    } finally {
      setSubscriptionLoading(false);
    }
  };

  return (
    <div style={{ 
      minHeight: 'calc(100vh - 120px)', 
      backgroundColor: 'var(--bg-color)',
      color: 'var(--text-color)',
      transition: 'background-color 0.3s ease, color 0.3s ease',
      padding: '20px 0'
    }}>
      <SEO
        title="Settings - Osailu"
        description="Configure your Osailu application settings including text-to-speech preferences."
        url="/settings"
      />
      <Container className="mt-5 mb-5" style={{ maxWidth: '800px', color: 'var(--text-color)' }}>
        <h1 className="mb-4" style={{ color: 'var(--text-color)' }}>
          <i className="fas fa-cog me-2"></i>
          Settings
        </h1>

        <Card className="mb-4">
          <Card.Header>
            <h5 className="mb-0">
              <i className="fas fa-volume-up me-2"></i>
              Text to Speech Settings
            </h5>
          </Card.Header>
          <Card.Body>
            {!("speechSynthesis" in window) ? (
              <div className="text-muted">
                <i className="fas fa-exclamation-triangle me-2"></i>
                Text to speech is not supported in this browser.
              </div>
            ) : (
              <>
                <Form.Group className="mb-3">
                  <Form.Label style={{ color: 'var(--text-color)' }}>Voice</Form.Label>
                  <Form.Select
                    value={voiceURI}
                    onChange={(e) => updateVoiceURI(e.target.value)}
                    style={{
                      backgroundColor: 'var(--card-bg)',
                      color: 'var(--text-color)',
                      borderColor: 'var(--border-color)',
                    }}
                  >
                    {voices.map((v) => (
                      <option key={v.voiceURI} value={v.voiceURI}>
                        {v.name} ({v.lang}){v.default ? " — default" : ""}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Text className="text-muted">
                    Select your preferred voice for text-to-speech.
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label style={{ color: 'var(--text-color)' }}>Speech Rate: {rate.toFixed(1.3)}</Form.Label>
                  <Form.Range
                    min="0.1"
                    max="10"
                    step="0.1"
                    value={rate}
                    onChange={(e) => updateRate(parseFloat(e.target.value))}
                    style={{
                      accentColor: 'var(--text-color)',
                    }}
                  />
                  <Form.Text className="text-muted">
                    Adjust the speed of speech (0.1 to 10).
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label style={{ color: 'var(--text-color)' }}>Pitch: {pitch.toFixed(1)}</Form.Label>
                  <Form.Range
                    min="0"
                    max="2"
                    step="0.1"
                    value={pitch}
                    onChange={(e) => updatePitch(parseFloat(e.target.value))}
                    style={{
                      accentColor: 'var(--text-color)',
                    }}
                  />
                  <Form.Text className="text-muted">
                    Adjust the pitch of speech (0 to 2).
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label style={{ color: 'var(--text-color)' }}>Volume: {volume.toFixed(1)}</Form.Label>
                  <Form.Range
                    min="0"
                    max="1"
                    step="0.1"
                    value={volume}
                    onChange={(e) => updateVolume(parseFloat(e.target.value))}
                    style={{
                      accentColor: 'var(--text-color)',
                    }}
                  />
                  <Form.Text className="text-muted">
                    Adjust the volume of speech (0 to 1).
                  </Form.Text>
                </Form.Group>
              </>
            )}
          </Card.Body>
        </Card>

        <Card className="mb-4">
          <Card.Header>
            <h5 className="mb-0">
              <i className="fas fa-envelope me-2"></i>
              Email & Subscription
            </h5>
          </Card.Header>
          <Card.Body>
            <Form.Group className="mb-3">
              <Form.Label style={{ color: 'var(--text-color)' }}>Email Address</Form.Label>
              <Form.Control
                type="email"
                placeholder="Enter your email address"
                value={email}
                onChange={(e) => updateEmail(e.target.value)}
                disabled={checkingStatus}
                style={{
                  backgroundColor: 'var(--card-bg)',
                  color: 'var(--text-color)',
                  borderColor: 'var(--border-color)',
                }}
              />
              <Form.Text className="text-muted">
                Your email address for notifications and account recovery.
              </Form.Text>
            </Form.Group>

            {checkingStatus && (
              <div className="mb-3">
                <Spinner size="sm" className="me-2" />
                <span style={{ color: 'var(--text-color)' }}>Checking subscription status...</span>
              </div>
            )}

            {subscriptionMessage.text && (
              <Alert variant={subscriptionMessage.type} className="mb-3">
                {subscriptionMessage.text}
              </Alert>
            )}

            <Form.Group className="mb-3">
              <Form.Check
                type="switch"
                id="subscription-switch"
                label="Subscribe to newsletter"
                checked={subscription}
                onChange={(e) => handleSubscriptionToggle(e.target.checked)}
                disabled={subscriptionLoading || checkingStatus || !email}
                style={{ color: 'var(--text-color)' }}
              />
              <Form.Text className="text-muted">
                Receive updates and news via email.
                {subscriptionLoading && <span className="ms-2"><Spinner size="sm" /></span>}
              </Form.Text>
            </Form.Group>

            {email && subscription && (
              <div className="mt-3">
                <Link 
                  to={`/unsubscribe?email=${encodeURIComponent(email)}`}
                  style={{ color: 'var(--text-color)', textDecoration: 'underline' }}
                >
                  <i className="fas fa-link me-2"></i>
                  Unsubscribe via email link
                </Link>
              </div>
            )}
          </Card.Body>
        </Card>

        <Card className="mb-4">
          <Card.Header>
            <h5 className="mb-0">
              <i className="fas fa-key me-2"></i>
              API Configuration
            </h5>
          </Card.Header>
          <Card.Body>
            <Form.Group className="mb-3">
              <Form.Label style={{ color: 'var(--text-color)' }}>ChatGPT API Key</Form.Label>
              <Form.Control
                type="password"
                placeholder="Enter your ChatGPT API key"
                value={chatgptApiKey}
                onChange={(e) => updateChatGPTApiKey(e.target.value)}
                style={{
                  backgroundColor: 'var(--card-bg)',
                  color: 'var(--text-color)',
                  borderColor: 'var(--border-color)',
                }}
              />
              <Form.Text className="text-muted">
                Your personal ChatGPT API key for enhanced features. Keep this secure.
              </Form.Text>
            </Form.Group>
          </Card.Body>
        </Card>

        <Card className="mb-4">
          <Card.Header>
            <h5 className="mb-0">
              <i className="fas fa-link me-2"></i>
              Anchor Text
            </h5>
          </Card.Header>
          <Card.Body>
            <Form.Group className="mb-3">
              <Form.Label style={{ color: 'var(--text-color)' }}>Anchor Text</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter your anchor text"
                value={anchorText}
                onChange={(e) => updateAnchorText(e.target.value)}
                style={{
                  backgroundColor: 'var(--card-bg)',
                  color: 'var(--text-color)',
                  borderColor: 'var(--border-color)',
                }}
              />
              <Form.Text className="text-muted">
                Custom anchor text for your links.
              </Form.Text>
            </Form.Group>
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
};

export default Settings;
